import { useRef, useState } from "react";
import {
  STRIPE_PUSH_POLL_MS,
  STRIPE_PUSH_WAIT_MS,
  type FieldChange,
  type StripePushOutcome,
} from "@/application/merchants/business_info_edit";
import type {
  InfoChangeAuditEntry,
  MerchantsRepository,
} from "@/application/merchants/merchants_repository";
import type { BusinessProfile } from "../../types";

export type InfoSaveRepo = Pick<
  MerchantsRepository,
  "getBusinessProfile" | "writeInfoChangeAudit" | "awaitStripePush"
>;

interface SaveRequest<Patch> {
  changes: FieldChange<string>[];
  /** Merge the patch into the live row rather than the one last seen. */
  readLiveRow: boolean;
  buildPatch: (row: BusinessProfile) => Patch;
  update: (patch: Patch) => Promise<BusinessProfile>;
  /** The business id and label come from the saved row, the changes from the request. */
  audit: Pick<InfoChangeAuditEntry, "action" | "extra">;
}

/**
 * The save path shared by the info edit dialogs: read the live row, update,
 * write the audit entry, then wait for the Stripe push result.
 */
export function useInfoSave(repo: InfoSaveRepo, business: BusinessProfile) {
  const [current, setCurrent] = useState(business);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [savedChanges, setSavedChanges] = useState<FieldChange<string>[]>([]);
  const [auditOk, setAuditOk] = useState(true);
  const [retryingAudit, setRetryingAudit] = useState(false);
  const [stripe, setStripe] = useState<StripePushOutcome | "checking">(
    "checking",
  );
  const auditEntry = useRef<InfoChangeAuditEntry | null>(null);

  /**
   * Resolves with the saved row once the update lands, or null after setting
   * `saveError`. The audit write and the Stripe wait carry on afterwards and
   * surface through `auditOk` and `stripe`.
   */
  async function save<Patch>(
    request: SaveRequest<Patch>,
  ): Promise<BusinessProfile | null> {
    setSaving(true);
    setSaveError(null);
    let row: BusinessProfile;
    try {
      const base = request.readLiveRow
        ? await repo.getBusinessProfile(current.id)
        : current;
      row = await request.update(request.buildPatch(base));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Unexpected error");
      setSaving(false);
      return null;
    }
    const entry: InfoChangeAuditEntry = {
      ...request.audit,
      businessId: row.id,
      targetLabel: row.businessName ?? "",
      changes: request.changes,
    };
    auditEntry.current = entry;
    setCurrent(row);
    setSaved(true);
    setSavedChanges(request.changes);
    setAuditOk(true);
    setStripe("checking");
    setSaving(false);
    void settle(row, entry);
    return row;
  }

  async function settle(row: BusinessProfile, entry: InfoChangeAuditEntry) {
    setAuditOk(await repo.writeInfoChangeAudit(entry));
    setStripe(
      await repo.awaitStripePush(row.id, row.updatedAt, {
        waitMs: STRIPE_PUSH_WAIT_MS,
        pollMs: STRIPE_PUSH_POLL_MS,
      }),
    );
  }

  async function retryAudit() {
    if (!auditEntry.current || retryingAudit) return;
    setRetryingAudit(true);
    setAuditOk(await repo.writeInfoChangeAudit(auditEntry.current));
    setRetryingAudit(false);
  }

  return {
    saving,
    saveError,
    clearSaveError: () => setSaveError(null),
    /** True once at least one save succeeded. */
    saved,
    savedChanges,
    auditOk,
    retryingAudit,
    stripe,
    save,
    retryAudit,
  };
}
